import { useEffect, useState } from 'react';
import { Banner, Button, Card, Dialog, Field, formatMoney, PageHeader, Pill } from '@elysian/ui';
import type { Account, Product, ProductImportResult, ProductInput, TaxCode } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso } from '../invoices/shared';
import { headingKey, parseCsv } from '../shared/csv';
import { Pager, ShowSize, SortTh, useClientList } from '../shared/ListControls';

const HEADINGS: Record<keyof Required<Pick<ProductInput, 'code' | 'description' | 'unit' | 'sales_price' | 'purchase_price' | 'sales_account'
  | 'purchase_account' | 'sales_tax_code' | 'purchase_tax_code'>>, string[]> = {
  code: ['code', 'product code', 'item code', 'sku', 'stock code', 'reference'],
  description: ['description', 'name', 'product', 'item', 'details'],
  unit: ['unit', 'units', 'uom', 'unit of measure'],
  sales_price: ['sales price', 'selling price', 'price', 'unit price', 'sale price'],
  purchase_price: ['purchase price', 'cost price', 'cost', 'buying price'],
  sales_account: ['sales account', 'income account', 'sales nominal', 'sales nominal code'],
  purchase_account: ['purchase account', 'expense account', 'purchase nominal', 'purchase nominal code'],
  sales_tax_code: ['sales vat', 'sales vat code', 'sales tax code', 'vat', 'vat code', 'tax code'],
  purchase_tax_code: ['purchase vat', 'purchase vat code', 'purchase tax code'],
};

const money = (v: string | null | undefined, base: string) => (v == null ? '—' : formatMoney(v, base));
const trim = (v: string | null | undefined) => (v == null ? '' : Number(v).toFixed(2));

/**
 * Products and services: a price list (no stock). Choosing an item on an
 * invoice or bill line fills it in; free-text lines keep working.
 */
export function Products() {
  const { source, can, context, today } = useSession();
  const base = context?.organisation.base_currency ?? 'EUR';
  const toast = useToast((t) => t.show);
  const [showOff, setShowOff] = useState(false);
  const [version, setVersion] = useState(0);
  const data = useAsync(async () => {
    const [products, income, expense, codes] = await Promise.all([
      source.products(true), source.accounts('income'), source.accounts('expense'), source.taxCodes(iso(today())),
    ]);
    return { products, income, expense, codes };
  }, [source, version], { keep: true });
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState('');
  const editable = can('contacts.write');
  const reload = () => setVersion((v) => v + 1);

  const d = data.state === 'ready' ? data.data : null;
  const off = d?.products.filter((p) => !p.is_active).length ?? 0;
  const matching = (d?.products ?? []).filter((p) => (p.is_active || showOff)
    && (!q.trim() || `${p.code} ${p.description}`.toLowerCase().includes(q.trim().toLowerCase())));
  // #49, #50: 50, 100 or all, and sorting on the headings, over the whole list.
  const list = useClientList(matching, {
    key: 'elysian.products', initial: { sort: 'code', dir: 'asc' }, filterKey: `${q}|${showOff}`,
    sorts: { code: (p) => p.code, description: (p) => p.description.toLowerCase(), unit: (p) => p.unit ?? '',
      sales: (p) => (p.sales_price === null || p.sales_price === undefined ? null : Number(p.sales_price)),
      purchase: (p) => (p.purchase_price === null || p.purchase_price === undefined ? null : Number(p.purchase_price)) },
  });
  const rows = list.rows;

  return (
    <>
      <PageHeader title="Products and services"
        subtitle="A price list for invoice and bill lines: choose an item and the line fills itself in. Stock is kept in Elysian ERP, not here."
        actions={editable && d ? <>
          <Button onClick={() => setImporting(true)}>Import from CSV</Button>
          <Button variant="primary" onClick={() => setEditing('new')}>Add an item</Button>
        </> : undefined} />
      {data.state === 'error' && <Banner>Couldn’t load the list: {data.error.message}</Banner>}
      {data.state === 'loading' && !d && <div className="el-skeleton" style={{ height: 200 }} aria-label="Loading the list" />}
      {d && d.products.length === 0 && (
        <Card padding="24px">
          <p style={{ margin: 0 }}><strong>No items yet.</strong></p>
          <p className="el-hint" style={{ margin: '6px 0 0' }}>
            Many businesses never need a list: lines can always be typed in. Add the things you sell or buy often, or import them from a spreadsheet
            (a CSV with columns such as code, description, sales price).
          </p>
        </Card>
      )}
      {d && d.products.length > 0 && (
        <Card padding="0 0 8px">
          <div className="el-spread" style={{ padding: '14px 16px' }}>
            <label className="el-search" style={{ flex: '0 1 320px', minWidth: 'min(260px, 100%)' }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code or description" aria-label="Search the list" />
            </label>
            <div className="el-row" style={{ gap: 14, alignItems: 'center' }}>
              {off > 0 && (
                <label className="el-check" style={{ minHeight: 0 }}>
                  <input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} />Show switched-off items ({off})
                </label>
              )}
              <ShowSize value={list.size} onChange={list.setSize} />
            </div>
          </div>
          <div className="el-table-wrap">
            <table className="el-table" aria-label="Products and services">
              <thead><tr>
                <SortTh label="Code" col="code" state={list.sortBy} onSort={list.setSortBy} />
                <SortTh label="Description" col="description" state={list.sortBy} onSort={list.setSortBy} />
                <SortTh label="Unit" col="unit" state={list.sortBy} onSort={list.setSortBy} />
                <SortTh label="Sales price" col="sales" state={list.sortBy} onSort={list.setSortBy} numeric />
                <SortTh label="Purchase price" col="purchase" state={list.sortBy} onSort={list.setSortBy} numeric />
                <th scope="col">VAT</th>
                <th scope="col"><span className="el-vh">Status</span></th></tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.code}>
                    <td><button type="button" className="el-linkbtn" onClick={() => setEditing(p)} aria-label={`${p.code}: ${p.description}`}>{p.code}</button></td>
                    <td>{p.description}</td>
                    <td>{p.unit ?? ''}</td>
                    <td className="el-num">{money(p.sales_price, base)}</td>
                    <td className="el-num">{money(p.purchase_price, base)}</td>
                    <td>{[p.sales_tax_code, p.purchase_tax_code].filter((x, i, a) => x && a.indexOf(x) === i).join(' / ')}</td>
                    <td>{!p.is_active ? <Pill tone="draft">Switched off</Pill> : p.source_system ? <span className="el-hint">From {p.source_system.replace('_', ' ')}</span> : null}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="el-muted">Nothing matches.</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager count={list.count} page={list.page} size={list.size} shown={rows.length} onPage={list.setPage} />
        </Card>
      )}
      {editing && d && (
        <ItemDialog item={editing === 'new' ? null : editing} income={d.income} expense={d.expense} codes={d.codes} editable={editable}
          onClose={() => setEditing(null)} onSaved={(msg) => { setEditing(null); toast(msg); reload(); }} />
      )}
      {importing && (
        <ImportDialog onClose={() => setImporting(false)} onDone={(r) => { setImporting(false); toast(`${r.added} added, ${r.updated} updated`); reload(); }} />
      )}
    </>
  );
}

function ItemDialog({ item, income, expense, codes, editable, onClose, onSaved }: {
  item: Product | null; income: Account[]; expense: Account[]; codes: TaxCode[]; editable: boolean;
  onClose: () => void; onSaved: (msg: string) => void;
}) {
  const { source } = useSession();
  const [f, setF] = useState({
    code: item?.code ?? '', description: item?.description ?? '', unit: item?.unit ?? '',
    sales_price: trim(item?.sales_price), purchase_price: trim(item?.purchase_price),
    sales_account: item?.sales_account ?? '', purchase_account: item?.purchase_account ?? '',
    sales_tax_code: item?.sales_tax_code ?? '', purchase_tax_code: item?.purchase_tax_code ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const salesCodes = codes.filter((c) => c.applies_to !== 'purchases');
  const purchaseCodes = codes.filter((c) => c.applies_to !== 'sales');

  async function run(fn: () => Promise<unknown>, msg: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onSaved(msg);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }
  const save = () => run(() => source.saveProduct(f.code.trim(), {
    description: f.description, unit: f.unit || null, sales_price: f.sales_price || null, purchase_price: f.purchase_price || null,
    sales_account: f.sales_account || null, purchase_account: f.purchase_account || null,
    sales_tax_code: f.sales_tax_code || null, purchase_tax_code: f.purchase_tax_code || null,
  }), item ? `${f.code.trim()} saved` : `${f.code.trim()} added`);

  return (
    <Dialog title={item ? `${item.code}: ${item.description}` : 'Add an item'} onClose={onClose}
      actions={<>
        {item && editable && (item.in_use
          ? <Button disabled={busy} onClick={() => void run(() => source.setProductActive(item.code, !item.is_active), item.is_active ? `${item.code} switched off` : `${item.code} switched back on`)}>
            {item.is_active ? 'Switch off' : 'Switch back on'}</Button>
          : <Button variant="danger" disabled={busy} onClick={() => void run(() => source.deleteProduct(item.code), `${item.code} deleted`)}>Delete</Button>)}
        <span style={{ flex: 1 }} />
        <Button onClick={onClose}>{editable ? 'Cancel' : 'Close'}</Button>
        {editable && <Button variant="primary" disabled={busy || !f.code.trim() || !f.description.trim()} onClick={() => void save()}>{item ? 'Save' : 'Add the item'}</Button>}
      </>}>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'contents' }} disabled={!editable}>
        <div className="el-form-grid">
          <Field id="pr-code" label="Code" hint={item ? 'Codes don’t change: add a new item instead' : 'Such as DET-OPT-01'}>
            <input id="pr-code" className="el-input" value={f.code} maxLength={40} disabled={!!item} onChange={(e) => set('code', e.target.value)} />
          </Field>
          <Field id="pr-unit" label="Unit" hint="each, hour, metre…">
            <input id="pr-unit" className="el-input" value={f.unit} maxLength={20} onChange={(e) => set('unit', e.target.value)} />
          </Field>
        </div>
        <Field id="pr-desc" label="Description" hint="What goes on the invoice or bill line">
          <input id="pr-desc" className="el-input" value={f.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <section aria-labelledby="pr-sales" className="el-stack" style={{ gap: 10 }}>
          <h3 id="pr-sales" className="el-label" style={{ margin: 0, fontSize: 14 }}>When you sell it</h3>
          <div className="el-form-grid">
            <Field id="pr-sp" label="Sales price"><input id="pr-sp" className="el-input el-num" inputMode="decimal" value={f.sales_price} onChange={(e) => set('sales_price', e.target.value)} /></Field>
            <Field id="pr-sa" label="Sales account">
              <select id="pr-sa" className="el-input" value={f.sales_account} onChange={(e) => set('sales_account', e.target.value)}>
                <option value="">Not set</option>{income.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
              </select>
            </Field>
            <Field id="pr-sv" label="Sales VAT">
              <select id="pr-sv" className="el-input" value={f.sales_tax_code} onChange={(e) => set('sales_tax_code', e.target.value)}>
                <option value="">Not set</option>{salesCodes.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </Field>
          </div>
        </section>
        <section aria-labelledby="pr-buy" className="el-stack" style={{ gap: 10 }}>
          <h3 id="pr-buy" className="el-label" style={{ margin: 0, fontSize: 14 }}>When you buy it</h3>
          <div className="el-form-grid">
            <Field id="pr-pp" label="Purchase price"><input id="pr-pp" className="el-input el-num" inputMode="decimal" value={f.purchase_price} onChange={(e) => set('purchase_price', e.target.value)} /></Field>
            <Field id="pr-pa" label="Purchase account">
              <select id="pr-pa" className="el-input" value={f.purchase_account} onChange={(e) => set('purchase_account', e.target.value)}>
                <option value="">Not set</option>{expense.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
              </select>
            </Field>
            <Field id="pr-pv" label="Purchase VAT">
              <select id="pr-pv" className="el-input" value={f.purchase_tax_code} onChange={(e) => set('purchase_tax_code', e.target.value)}>
                <option value="">Not set</option>{purchaseCodes.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </Field>
          </div>
        </section>
      </fieldset>
      {item?.in_use && <p className="el-hint" style={{ margin: 0 }}>On invoices or bills already, so it can be switched off but not deleted. Changes apply to new lines only.</p>}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

function ImportDialog({ onClose, onDone }: { onClose: () => void; onDone: (r: ProductImportResult) => void }) {
  const { source } = useSession();
  const [text, setText] = useState('');
  const [name, setName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ProductImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ProductInput[]>([]);
  const [unknown, setUnknown] = useState<string[]>([]);

  useEffect(() => {
    setResult(null);
    const table = parseCsv(text);
    if (table.length < 2) { setRows([]); setUnknown([]); return; }
    const keys = table[0]!.map((h) => headingKey(h, HEADINGS));
    setUnknown(table[0]!.filter((_, i) => !keys[i] && table[0]![i]!.trim()));
    setRows(table.slice(1).map((r) => {
      const o: Record<string, string> = {};
      keys.forEach((k, i) => { if (k && r[i]?.trim()) o[k] = r[i]!.trim(); });
      return o as ProductInput;
    }));
  }, [text]);

  const hasCode = rows.length > 0 && rows.every((r) => r.code);
  async function run() {
    setBusy(true);
    setError(null);
    try {
      const r = await source.importProducts(rows);
      if (r.errors.length) setResult(r); else onDone(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="Import items from a CSV file" onClose={onClose}
      actions={<><Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !hasCode} onClick={() => void run()}>{busy ? 'Importing…' : `Import ${rows.length || ''} ${rows.length === 1 ? 'item' : 'items'}`}</Button></>}>
      <p className="el-hint" style={{ margin: 0 }}>
        The first row names the columns: code and description, and any of unit, sales price, purchase price, sales account, purchase account, sales VAT,
        purchase VAT. Items whose code is already on the list are updated. Nothing is saved unless every row is fine.
      </p>
      <Field id="imp-file" label="CSV file">
        <input id="imp-file" type="file" accept=".csv,text/csv" className="el-input" style={{ paddingTop: 10 }}
          onChange={(e) => { const file = e.target.files?.[0]; if (file) { setName(file.name); void file.text().then(setText); } }} />
      </Field>
      <Field id="imp-text" label="Or paste it here">
        <textarea id="imp-text" className="el-input" rows={5} value={text} onChange={(e) => { setName(null); setText(e.target.value); }}
          placeholder={'code,description,unit,sales price,sales vat\nDET-OPT,Optical smoke detector,each,42.50,S23'} />
      </Field>
      {rows.length > 0 && (
        <p className="el-hint" style={{ margin: 0 }} aria-live="polite">
          {name ? `${name}: ` : ''}{rows.length} {rows.length === 1 ? 'row' : 'rows'} found.
          {!hasCode && ' Every row needs a code (a column headed “code”).'}
          {unknown.length > 0 && ` Columns left out: ${unknown.join(', ')}.`}
        </p>
      )}
      {result && (
        <div className="el-error-box" role="alert">
          <strong>{result.errors.length} {result.errors.length === 1 ? 'row needs' : 'rows need'} fixing; nothing was imported.</strong>
          <ul className="el-errors">
            {result.errors.slice(0, 20).map((e) => <li key={e.row}>Row {e.row}{e.code ? ` (${e.code})` : ''}: {e.error}</li>)}
          </ul>
          {result.errors.length > 20 && <p style={{ margin: 0 }}>…and {result.errors.length - 20} more.</p>}
        </div>
      )}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
